import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "1",
  "slug": "introducing-streak-mode",
  "title": "Introducing Streak Mode: How Far Can You Go?",
  "excerpt": "We built a new way to play — survive as many rounds as you can by guessing within the distance threshold. One wrong guess and it's over.",
  "date": "2026-03-18",
  "category": "Updates",
  "image": "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&q=80",
  "readTime": "4 min",
  "featured": true,
  "content": [
    {
      "type": "paragraph",
      "text": "Most geography games end after a fixed number of rounds. Classic mode in EarthGuessr is five locations long — five satellite frames, five guesses, one final score. It works, but it has a ceiling. The best you can do is the best of five. We wanted something with no ceiling at all, where the question is not \"how good was your run today\" but \"how far can you keep going before you break\". Streak Mode is the answer."
    },
    {
      "type": "heading",
      "text": "The rules"
    },
    {
      "type": "paragraph",
      "text": "One satellite frame at a time. You guess. If your guess is within the distance threshold you set at the start, you survive and a new location loads. If your guess is over the threshold, the streak ends. There is no \"best of\" — every round is do-or-die. The only question is how many you can chain together."
    },
    {
      "type": "paragraph",
      "text": "When you start a streak, you pick from three distance thresholds: 1,000 km, 1,500 km, or 2,000 km. These are not interchangeable difficulties — they are three separate ladders. Your best streak at 1,000 km is tracked independently from your best streak at 2,000 km, because they reward different skills. A 2,000 km threshold lets you survive on \"right continent, roughly right region\" guesses. A 1,000 km threshold demands \"right country, often right province\". The gap between Madrid and Lisbon is about 600 km, so getting within 1,000 km of a target on the Iberian peninsula means you actually have to identify it as Iberian, not just \"Europe\"."
    },
    {
      "type": "heading",
      "text": "Why a threshold, not a score"
    },
    {
      "type": "paragraph",
      "text": "Classic EarthGuessr awards points on a curve — 5,000 for a perfect guess, decaying exponentially to 0 by 10,000 km. The scoring is forgiving by design. Even a wild guess that lands on the wrong continent can pick up a few hundred points. That works for \"play five rounds and see your score\". It does not work for a survival mode, because there is no clean line between \"alive\" and \"dead\"."
    },
    {
      "type": "paragraph",
      "text": "A distance threshold gives you that line. Either your guess is within the limit or it is not. There is no ambiguity, no partial credit, no \"well, I was close-ish\". That binary is what makes streak mode tense — every guess is pass-or-fail and you know exactly what you have to clear."
    },
    {
      "type": "heading",
      "text": "What streak mode tests that classic does not"
    },
    {
      "type": "paragraph",
      "text": "Five-round classic rewards your peak performance. You can get lucky once, identify one location perfectly, and your average drags up. A long streak rewards your floor — your worst guess has to be good enough. This is the harder skill. It means you can never coast on a frame you do not know, because that frame ends the run. You either learn to read every kind of satellite imagery, or you cap out quickly."
    },
    {
      "type": "paragraph",
      "text": "Different thresholds also reward different habits. At 2,000 km you can play loose — wide guesses, fast decisions, trust the obvious cues. At 1,000 km you have to slow down and look for the second or third confirmation before you click. Players who are very good at one threshold often plateau at the next tighter one, because the playstyle that worked at the looser threshold becomes a liability when the margin shrinks."
    },
    {
      "type": "heading",
      "text": "How to build a long streak"
    },
    {
      "type": "list",
      "items": [
        "Start at 2,000 km. Get a feel for how survival mode plays before tightening the threshold. The first run or two is almost always shorter than you expect.",
        "Identify the climate band before anything else. You will burn seconds looking at fine details on a frame from the wrong climate. Sky colour, vegetation density, soil tone, and building style all read fast at low zoom.",
        "When in doubt, guess into high-density land. If you cannot identify a frame and the threshold is 2,000 km, putting your guess in central Europe or the central United States covers more land than any other single guess you could make.",
        "Use the full 90 seconds. Most eliminations come from rushing, not from running out of time. A second look at the road network or the field shape often turns a guess from \"probably wrong\" into \"almost certainly right\"."
      ]
    },
    {
      "type": "heading",
      "text": "The three-records problem"
    },
    {
      "type": "paragraph",
      "text": "One thing we did not fully anticipate when we shipped streak mode: serious players want to know which of their three records is most impressive, and there is no clean answer. Twenty rounds at 1,000 km, twelve rounds at 1,500 km, and forty rounds at 2,000 km are roughly equivalent skill levels — but you cannot tell that from the raw numbers. We are looking at ways to normalise this in a future update, possibly with a single \"streak rating\" derived from the three personal bests. For now the threshold sits next to the number on your profile so you can show your work."
    },
    {
      "type": "heading",
      "text": "One more thing"
    },
    {
      "type": "paragraph",
      "text": "Streak mode has one small mechanical decision that turns out to matter a lot. When you fail, your run is over instantly — but you still see the actual location on the map, and you still see exactly how far off you were. Almost every long streak run ends with a player staring at a frame they could have identified if they had spent ten more seconds on it. That information is the point. You stop, you look at the location, you realise what you missed, and the next streak benefits."
    },
    {
      "type": "paragraph",
      "text": "Streak Mode is live now. Pick a threshold and see how long you can stay alive."
    }
  ]
};
